/* eslint-disable no-continue */
import * as types from './types';
import helper from '@/core/helper';

const getters = {
  [types.GET_IN_PROGRESS_CONTENT_ITEMS]: (state) => {
    const {
      newScrapedContent, inProgressContentScrapes,
    } = state;
    if (newScrapedContent.length === 0) return [];
    const inProgressArticles = [];
    if (inProgressContentScrapes.contentItems.length !== 0) {
      inProgressContentScrapes.contentItems.forEach((contentItem) => {
        inProgressArticles.push({
          title: contentItem.url, // Remove after deleting the old components (after part 4 is complete)
          url: contentItem.url,
          imageUrl: 'loading',
          state: contentItem.status.replace('_TRANSCRIPTION', '').replaceAll('_', ' '),
          updatedDate: new Date(),
        });
      });
    }
    return inProgressArticles;
  },
  [types.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS]: (state) => {
    if (state.newContentItems.length === 0) return [];
    return state.newContentItems;
  },
  [types.GET_FAILED_SCRAPED_CONTENT_ITEMS]: (state) => {
    const { newScrapedContent } = state;
    if (!newScrapedContent.length) return [];
    const failedArticles = newScrapedContent.filter((scrapedArticle) => scrapedArticle.status.includes('FAILED'));
    return failedArticles.map((item) => ({
      id: item.id,
      url: item.url,
      imageUrl: item.imageUrl || '/images/image-not-found.png',
      state: 'FAILED',
      createdDate: item.createdDate,
      updatedDate: item.createdDate,
    }));
  },
  [types.GET_CONTENT_ITEM_DISPLAY_PROPERTIES]: (state) => (contentItemUuid, creativeId) => {
    const { contentItemsDisplayProperties } = state;
    if (!helper.objLength(contentItemsDisplayProperties) || !creativeId || !contentItemsDisplayProperties[creativeId]) return {};
    return contentItemsDisplayProperties[creativeId][contentItemUuid]?.displayProperties;
  },
  [types.GET_CURRENT_ASSET_NAMES]: (state) => {
    const { creativeTypeSelected, contentItemsDisplayProperties } = state;
    const imageDisplayPropertiesName = creativeTypeSelected === 'IMAGE' ? 'creativeImage' : 'imageFileName';
    return Object.values(contentItemsDisplayProperties)
      .flatMap((creative) => Object.values(creative))
      .map((contentItem) => contentItem.displayProperties)
      .map((displayProperties) => displayProperties[imageDisplayPropertiesName])
      .filter((imageLink) => imageLink !== undefined)
      .map((imageLink) => imageLink.split('/').pop());
  },
  [types.GET_CONTENT_ITEM_ACTIVE_STATE]: (state) => (contentItemUuid, creativeId) => {
    if (!helper.objLength(state.contentItemsDisplayProperties) || !creativeId || !state.contentItemsDisplayProperties[creativeId]) return true;
    return state.contentItemsDisplayProperties[creativeId][contentItemUuid]?.active;
  },
  [types.GET_ARTICLE_BY_ID]: (state, getter) => (id) => {
    if (state.contentEditorArticle.id === id) return state.contentEditorArticle;
    return getter.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS.find((article) => article.id === id)
      || getter.GET_FAILED_SCRAPED_CONTENT_ITEMS.find((article) => article.id === id);
  },
  [types.GET_ARTICLE_BY_UUID]: (state) => (uuid) => state.newContentItems.find((article) => article.encodedExternalId === uuid),
  [types.GET_ARTICLES]: (state) => (isDraft) => {
    const { scrapedContent, contentItems } = state;
    if (!scrapedContent.length) {
      return [];
    }
    const articles = scrapedContent
      .map((scrapedArticle) => {
        let contentItem = Array.from(contentItems)
          .find((item) => item.id === scrapedArticle.contentItemId);
        if (contentItem === undefined && state.query === '' && isDraft) {
          contentItem = {
            title: 'Item not found',
            imageUrl: '/images/image-not-found.png',
            url: scrapedArticle.url,
          };
        }
        if (contentItem === undefined) return undefined;
        let edited;
        if (contentItem) {
          edited = contentItem.updatedDate ? helper.parseISOString(contentItem.updatedDate) : 0;
        }
        return {
          ...scrapedArticle,
          ...contentItem,
          edited,
        };
      });
    return articles.filter((item) => item);
  },
  [types.GET_CREATIVES_FULL_LIST_PROPERTIES]: (state) => state.creatives.filter((creative) => creative.paused === false),
  [types.GET_ACTIVE_CREATIVES]: (state) => state.creatives.filter((creative) => creative.paused === false),
  [types.GET_CREATIVES_DEFINED]: (state) => state.creatives && state.creatives.length > 0,
  [types.GET_TEMPLATE_OPTIONS]: (state) => {
    if (!state.creativeTemplates.length) {
      return [];
    }
    const templateOptions = [];
    state.creativeTemplates.map((template) => {
      templateOptions.push({ value: template.id, text: `${template.width} x ${template.height}` });
      return template;
    });
    return templateOptions;
  },
  [types.GET_ACTIVE_CREATIVE_TEMPLATES]: (state, getter) => {
    const creativesTemplates = {};
    state.creativesSizes.forEach((size) => {
      creativesTemplates[`${size.width}x${size.height}`] = [];
    });
    getter.GET_ACTIVE_CREATIVES
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((creative) => {
        const creativeTemplate = state.creativeTemplates.find((template) => template.id === creative.creativeTemplateId);
        if (!creativeTemplate) return;
        const targetArray = creativesTemplates[`${creativeTemplate.width}x${creativeTemplate.height}`];
        targetArray.push({
          name: getter.GET_TEMPLATE_NAME_BY_ID(creative.creativeTemplateId),
          realName: creative.name,
          creativeId: creative.id,
          creativeKey: creative.creativeKey,
          sizeId: creativeTemplate.sizeId,
          width: creativeTemplate.width,
          height: creativeTemplate.height,
        });
      });
    Object.values(creativesTemplates).forEach((array) => {
      if (array.length > 1) {
        array.forEach((creativeTemplate, index) => {
          creativeTemplate.name += ` - ${index + 1}`;
        });
      }
    });
    return Object.values(creativesTemplates).flat();
  },
  [types.GET_TEMPLATE_NAME_BY_ID]: (state) => (templateId) => {
    const creativeTemplate = state.creativeTemplates.find((template) => template.id === templateId);
    const creativeSize = state.creativesSizes.find((size) => size.id === creativeTemplate.sizeId);
    return `${creativeSize.name} (${creativeSize.width}x${creativeSize.height})`;
  },
  [types.GET_ANNOTATIONS]: (state) => {
    if (!state.currentComments.length) {
      return [];
    }
    return state.currentComments
      .filter((comment) => !!comment.commentLocation)
      .map((comment) => comment.commentLocation);
  },
  [types.GET_CURRENT_SELECTED_CREATIVE]: (state) => {
    if (state.selectedCreativeId === 0 || state.creatives.length === 0) {
      return {};
    }
    const creativeIndex = state.creatives.findIndex((item) => item.id === state.selectedCreativeId);
    return state.creatives[creativeIndex];
  },
  [types.GET_CURRENT_SELECTED_CREATIVE_PROPERTIES]: (state, getter) => getter.GET_CURRENT_SELECTED_CREATIVE?.properties,
  [types.GET_CURRENT_SELECTED_CREATIVE_DISPLAY_COPY]: (state) => (contentItemUuid) => {
    if (state.selectedCreativeId === 0 || Object.keys(state.allDisplayCopy).length === 0 || !contentItemUuid) {
      return {};
    }
    return state.allDisplayCopy?.[contentItemUuid]?.[state.selectedCreativeId] || {};
  },
  [types.GET_CREATIVES_CARROUSEL_COVERED]: (state) => state.creativeBottomPanelIsOpen || state.creativeSidebarIsOpen,
  [types.GET_CONTENT_ITEMS_WITH_MISSED_CREATIVES]: (state) => {
    if (state.creativeTypeSelected !== 'IMAGE') return [];
    const { contentItemsDisplayProperties, activeCreatives, newContentItems } = state;

    if (activeCreatives.length === 0 || newContentItems.length === 0 || contentItemsDisplayProperties.length === 0) return [];
    const activeCreativeIds = activeCreatives.map((creative) => creative.id);
    const contentItemUuids = newContentItems.map((contentItem) => contentItem.encodedExternalId);

    const contentItemsWithMissedCreatives = new Set();
    activeCreativeIds.map((creativeId) => contentItemsDisplayProperties[creativeId])
      .filter((creativeDisplayProperties) => creativeDisplayProperties !== undefined)
      .flatMap((creativeDisplayProperties) => contentItemUuids.map((contentItemUuid) => creativeDisplayProperties[contentItemUuid]))
      .filter((properties) => properties !== undefined)
      .forEach((properties) => {
        const isCreativeImagePresent = Object.keys(properties.displayProperties).includes('creativeImage');
        if (!isCreativeImagePresent) contentItemsWithMissedCreatives.add(properties.contentItemUuid);
      });
    return Array.from(contentItemsWithMissedCreatives);
  },
};

export default getters;
